import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";

const SUPPORTED = ["fr", "nl", "en"];
const SITE = process.env.REACT_APP_PUBLIC_URL || "https://www.rycm.be";

/* Detects /fr|/nl|/en prefix in the URL pathname and syncs the language context.
   Also rewrites legacy URLs (no prefix) to include the current language prefix. */
export function useLangUrlSync() {
  const { lang, setLang } = useLang();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const seg = pathname.split("/").filter(Boolean)[0];
    if (SUPPORTED.includes(seg)) {
      if (seg !== lang) setLang(seg);
      return;
    }
    // Legacy URL — silently rewrite to current language prefix (won't add to history)
    const target = `/${lang}${pathname === "/" ? "" : pathname}${search}${hash}`;
    navigate(target, { replace: true });
  }, [pathname, lang, setLang, navigate, search, hash]);
}

/* Strips the language prefix from a pathname. */
export function stripLangPrefix(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (SUPPORTED.includes(parts[0])) {
    return "/" + parts.slice(1).join("/");
  }
  return pathname || "/";
}

/* Builds a language-prefixed path (used by Link components). */
export function withLang(lang, path) {
  if (!path) return `/${lang}`;
  if (path.startsWith("http")) return path;
  const clean = path.startsWith("/") ? path : `/${path}`;
  // Strip existing prefix if any
  const noPrefix = stripLangPrefix(clean);
  return `/${lang}${noPrefix === "/" ? "" : noPrefix}`;
}

const PAGE_BY_PATH = {
  "/": "home", "/club": "club", "/voile": "voile", "/motor": "motor",
  "/stages": "stages", "/regattas": "regattas", "/agenda": "agenda",
  "/galerie": "gallery", "/gallery": "gallery", "/contact": "contact", "/shop": "shop",
};

export default function SeoHead({ title, description, image, type = "website", keywords }) {
  const { lang, seo } = useLang();
  const { pathname } = useLocation();
  const cleanPath = stripLangPrefix(pathname);

  const pageKey = PAGE_BY_PATH[cleanPath];
  const ov = pageKey ? seo(pageKey) : {};
  const cmsTitle = ov[`title_${lang}`];
  const cmsDesc = ov[`desc_${lang}`];

  const canonical = `${SITE}/${lang}${cleanPath === "/" ? "" : cleanPath}`;
  const siteName = "Royal Yacht Club de la Meuse — RYCM";
  const effectiveTitle = cmsTitle || title;
  const fullTitle = effectiveTitle ? `${effectiveTitle} · ${siteName}` : siteName;
  const desc = cmsDesc || description || {
    fr: "Royal Yacht Club de la Meuse — Fondé en 1928, école de voile, régates, stages et événements nautiques sur la Meuse à Liège.",
    nl: "Royal Yacht Club de la Meuse — Opgericht in 1928, zeilschool, regatta's, stages en watersportevenementen op de Maas in Luik.",
    en: "Royal Yacht Club de la Meuse — Founded in 1928, sailing school, regattas, camps and nautical events on the Meuse river in Liège, Belgium.",
  }[lang];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {SUPPORTED.map((l) => (
        <link
          key={l} rel="alternate" hrefLang={l}
          href={`${SITE}/${l}${cleanPath === "/" ? "" : cleanPath}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE}/fr${cleanPath === "/" ? "" : cleanPath}`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={{ fr: "fr_BE", nl: "nl_BE", en: "en_GB" }[lang]} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      {image && <meta property="og:image" content={image.startsWith("http") ? image : `${SITE}${image}`} />}

      {/* Twitter */}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {image && <meta name="twitter:image" content={image.startsWith("http") ? image : `${SITE}${image}`} />}
    </Helmet>
  );
}
